<template>
  <div :key="`resource-menu-${uid}`" class="resource-menu">
    <item-header
      type="resource"
      :choosable="true"
      :selected-id="ctx.resource.id"
      @select="selectResource"
      @close="$emit('close')"
    >
      <template #content>
        <editable-item-title :item="ctx.resource" />
      </template>
    </item-header>

    <!-- GENERAL OPTIONS -->
    <drawer
      v-model:open="drawerStates.resourceGeneral"
      :title="$t('labels.general')"
    >
      <div class="column gap-4">
        <input-text
          :label="$t('labels.title')"
          :value="ctx.resource.title"
          @enter="descriptionField?.focus()"
          @update:value="ctx.updateKey('title', $event)"
        />

        <input-texteditor
          ref="descriptionField"
          :label="$t('labels.description')"
          :value="ctx.resource.description"
          @update:value="ctx.updateKey('description', $event)"
        />
      </div>
    </drawer>

    <drawer
      v-model:open="drawerStates.resourceState"
      :title="$t('labels.valueScope')"
    >
      <div class="column gap-4">
        <div>
          <div class="line-docanchor">
            <label class="label">{{ $t("words.startValue") }}</label>
            <doc-anchor :object="ctx.resource" prop="value" />
          </div>
          <input-number
            :min="ctx.resource.minValue"
            :max="ctx.resource.maxValue"
            :value="ctx.resource.value"
            step="any"
            @update:value="ctx.updateKey('value', $event)"
          />
        </div>

        <input-text
          :label="$t('labels.unit')"
          :value="ctx.resource.unit"
          @update:value="ctx.updateKey('unit', $event)"
        />

        <div class="line gap-4">
          <input-number
            :label="$t('labels.minValue')"
            :value="ctx.resource.minValue"
            :max="ctx.resource.maxValue"
            :min="null"
            step="any"
            @update:value="updateMinValue"
            @blur="onMinMaxBlur"
          />
          <input-number
            :label="$t('labels.maxValue')"
            :value="ctx.resource.maxValue"
            :min="ctx.resource.minValue"
            :max="null"
            step="any"
            @update:value="updateMaxValue"
            @blur="onMinMaxBlur"
          />
        </div>
      </div>
    </drawer>

    <!-- RELATIONS -->
    <drawer
      v-if="features.impact"
      v-model:open="drawerStates.resourceRelations"
      :title="$t('objectNames.impact', 3)"
    >
      <div class="column gap-6 mt-1">
        <relation-list
          type="outgoing"
          :element-uid="ctx.resource.uid"
          :selected-relation-id="selectedRelation?.id"
          @select="emit('select', $event)"
        />

        <relation-list
          type="incoming"
          :element-uid="ctx.resource.uid"
          :selected-relation-id="selectedRelation?.id"
          @select="emit('select', $event)"
        />
      </div>
    </drawer>

    <drawer
      v-if="features.flow"
      v-model:open="drawerStates.resourceFlows"
      :title="$t('objectNames.flow', 5)"
    >
      <flow-list
        element-type="resource"
        :element-id="ctx.resource.id"
        :sub-selection="subSelection"
        @select="emit('select', $event)"
      />
    </drawer>

    <!-- MOMENTUM -->
    <drawer
      v-if="features.momentum"
      :title="$t('objectNames.momentum')"
      v-model:open="drawerStates.resourceMomentum"
    >
      <button
        v-if="momentum == null"
        type="button"
        class="button"
        @click.prevent="createMomentum"
      >
        {{ $t("actions.createMomentum") }}
      </button>
      <button-item
        v-if="momentum"
        class="mt-2"
        :icons="['trash-2']"
        :selected="
          subSelection?.type == 'momentum' && subSelection?.id == momentum.id
        "
        @click:trash-2="deleteMomentum()"
        @click.prevent="emit('select', momentum)"
      >
        {{ $t("labels.editX", [$t("objectNames.momentum")]) }}
      </button-item>
    </drawer>

    <drawer
      v-if="features.stakeholder"
      v-model:open="drawerStates.resourceStakeholders"
      :title="$t('objectNames.stakeholder', 2)"
    >
      <stakeholder-list
        item-type="resource"
        :item-id="ctx.resource.id"
        @select="$emit('select', $event)"
        @deselect="$emit('select', null)"
      />
    </drawer>

    <drawer
      v-if="features.design"
      v-model:open="drawerStates.resourceDesign"
      :title="$t('labels.design')"
    >
      <element-design-menu :element-uid="ctx.resource.uid" />
    </drawer>

    <menu-group class="mt-auto">
      <button
        class="button button_secondary mv-2"
        type="button"
        @click="deleteResource"
      >
        {{ $t("actions.deleteResource", 1) }}
      </button>
    </menu-group>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { splitUid } from "@/helpers/utility/Id";

import InputText from "@core/input/InputText.vue";
import InputTexteditor from "@core/input/TextEditor.vue";
import InputNumber from "@core/input/InputNumber.vue";

import Drawer from "@core/ui/DrawerBlock.vue";
import MenuGroup from "@core/ui/MenuGroup.vue";
import ButtonItem from "@core/input/ButtonItem.vue";

import ItemHeader from "@editor/components/ItemHeader.vue";
import DocAnchor from "@editor/ui/DocumentationAnchor.vue";
import FlowList from "@editor/items/flow/FlowList.vue";
import RelationList from "@editor/items/relation/RelationList.vue";
import EditableItemTitle from "@editor/components/EditableItemTitle.vue";
import StakeholderList from "@editor/items/stakeholder/StakeholderList.vue";
import ElementDesignMenu from "@editor/items/element/ElementDesignMenu.vue";

import { useResourceStore } from "./resources.store";
import { useRelationStore } from "@editor/items/relation/relations.store";
import { useDrawerStates } from "@editor/store/editor/drawerStates.store";
import { useResource } from "@editor/items/resource/useResource";
import { useFeatures } from "@editor/store/features.store";
import { useMomentumStore } from "../momentum/momentums.store";
import { useSelectionStore } from "@editor/store/editor/selection.store";
import type { Uid } from "@/helpers/utility/Id";

interface Props {
  uid: Uid<"resource">;
  nodeUid?: Uid<"sceneNode">;
  subSelection?: null | { type: string; id: number };
}
const props = withDefaults(defineProps<Props>(), {
  subSelection: null
});

const emit = defineEmits<{
  (
    e: "select",
    item: {
      type: "relation" | "stakeholder" | "flow" | "momentum";
      id: number;
    } | {
      uid: Uid<"relation" | "stakeholder" | "flow" | "momentum">;
    } | null
  ): void;
  (e: "deselect", item: { type: string; id: number }): void;
  (e: "select-main", item: { type: string; id: number }): void;
  (e: "delete"): void;
  (e: "close"): void;
}>();

const features = useFeatures();
const drawerStates = useDrawerStates();
const relationStore = useRelationStore();

const ctx = useResource(() => splitUid(props.uid).id);
const selectionStore = useSelectionStore();

function deleteResource() {
  emit("delete");
  ctx.delete();
}

const descriptionField = ref<InstanceType<typeof InputTexteditor>>();

function selectResource(resourceId: number | null) {
  if (resourceId === ctx.resource.id) return;
  if (resourceId == null) {
    selectionStore.clearAll();
    return;
  }

  const resource = useResourceStore().items[resourceId];
  if (resource == null) {
    selectionStore.clearAll();
  } else {
    selectionStore.select(resource);
  }
}

// ===================
// STATE

function updateMinValue(value: number) {
  const update: {
    minValue: number;
  } = {
    minValue: value
  };
  const maxValue = ctx.resource.maxValue || Infinity;
  if (value > maxValue) return;
  ctx.update(update);
}

function updateMaxValue(value: number) {
  const update: {
    maxValue: number;
  } = {
    maxValue: value
  };
  const minValue = ctx.resource.minValue || -Infinity;
  if (value < minValue) return;
  ctx.update(update);
}

function onMinMaxBlur() {
  return;
}

// ===================
// RELATIONS

const selectedRelation = computed(() => {
  if (props.subSelection == null) return null;
  if (props.subSelection.type != "relation") return null;
  return relationStore.items[props.subSelection.id];
});

// =====================
// MOMENTUM

const momentum = computed(() => {
  return useMomentumStore().byElementUid[ctx.resource.uid];
});

async function createMomentum() {
  if (momentum.value != null) {
    return;
  }
  const newMomentum = await useMomentumStore().create({
    elementType: "resource",
    elementId: ctx.resource.id,
  });
  emit("select", { type: "momentum", id: newMomentum.id });
}

function deleteMomentum() {
  if (momentum.value != null) {
    emit("deselect", { type: "momentum", id: momentum.value.id });
    useMomentumStore().delete(momentum.value);
  }
}
</script>

<style>
.resource-menu {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}
</style>
